import { useMemo } from "react";

// Diff mot à mot entre deux textes (LCS). Renvoie une liste de segments {type, text}
// type ∈ { eq, add, del }. Les espaces sont conservés comme jetons.
export function diffWords(a, b) {
  const tok = (s) => (s || "").match(/\s+|[^\s]+/g) || [];
  const A = tok(a);
  const B = tok(b);
  const n = A.length;
  const m = B.length;
  // garde-fou : au-delà, on n'affiche pas de diff détaillé (perf)
  if (n * m > 4_000_000) return null;
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  const push = (type, text) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { push("eq", A[i]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push("del", A[i]); i++; }
    else { push("add", B[j]); j++; }
  }
  while (i < n) { push("del", A[i]); i++; }
  while (j < m) { push("add", B[j]); j++; }
  return out;
}

const isWord = (t) => /[^\s]/.test(t);

export function WordDiff({ original, revised, className = "", testid }) {
  const parts = useMemo(() => diffWords(original, revised), [original, revised]);
  const stats = useMemo(() => {
    if (!parts) return { add: 0, del: 0 };
    let add = 0;
    let del = 0;
    for (const p of parts) {
      if (!isWord(p.text)) continue;
      const w = (p.text.match(/[^\s]+/g) || []).length;
      if (p.type === "add") add += w;
      else if (p.type === "del") del += w;
    }
    return { add, del };
  }, [parts]);

  if (!parts) {
    return (
      <div data-testid={testid} className={className}>
        <p className="whitespace-pre-wrap">{revised}</p>
      </div>
    );
  }

  return (
    <div data-testid={testid} className={className}>
      <div className="mb-2 flex flex-wrap items-center gap-3 text-xs" data-testid="rs-diff-stats">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">+{stats.add} ajout{stats.add > 1 ? "s" : ""}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-600">−{stats.del} suppression{stats.del > 1 ? "s" : ""}</span>
        <span className="text-slate-400">Vert = ajouté · Rouge barré = supprimé</span>
      </div>
      <p className="whitespace-pre-wrap leading-relaxed">
        {parts.map((p, k) => {
          if (p.type === "eq") return <span key={k}>{p.text}</span>;
          if (p.type === "add") {
            return <span key={k} className="rounded-sm bg-emerald-100 text-emerald-800">{p.text}</span>;
          }
          if (!isWord(p.text)) return <span key={k}>{p.text}</span>;
          return <del key={k} className="rounded-sm bg-red-100 text-red-500 line-through decoration-red-400/70">{p.text}</del>;
        })}
      </p>
    </div>
  );
}
